import { useCallback } from 'react';

/**
 * 多模态工具方法 Hook
 * 提供状态判断、数据转换等工具方法
 */
const useMultimodalUtils = () => {
    /**
     * 工具方法：状态判断
     */
    const isSuccessStatus = useCallback((status) => {
        return status === 1 || status === 'completed' || status === 'success' || status === 'done';
    }, []);

    const isProcessingStatus = useCallback((status) => {
        return status === 2 || status === 'pending' || status === 'processing';
    }, []);

    const isFailedStatus = useCallback((status) => {
        return status === 3 || status === 'failed' || status === 'error';
    }, []);

    /**
     * 工具方法：从响应中提取状态
     */
    const extractStatus = useCallback((payload, taskType) => {
        if (!payload) return undefined;

        const resolveStatusValue = (source) => {
            if (!source) return undefined;
            return source.status ?? source.task_status ?? source.state;
        };

        if (Array.isArray(payload)) {
            const target = payload.find((item) => Number(item.task_type) === Number(taskType));
            return resolveStatusValue(target);
        }

        if (Array.isArray(payload?.data)) {
            const target = payload.data.find((item) => Number(item.task_type) === Number(taskType));
            if (target) {
                return resolveStatusValue(target);
            }
        }

        if (payload.task_list && Array.isArray(payload.task_list)) {
            const target = payload.task_list.find((item) => Number(item.task_type) === Number(taskType));
            if (target) {
                return resolveStatusValue(target);
            }
        }

        if (taskType !== undefined && payload?.task_type !== undefined && Number(payload.task_type) === Number(taskType)) {
            return resolveStatusValue(payload);
        }

        return resolveStatusValue(payload) ?? resolveStatusValue(payload?.data);
    }, []);

    /**
     * 工具方法：卡片类型到 task_type 的映射
     */
    const getTaskType = useCallback((cardType) => {
        const typeMap = {
            'text_summary': 1,  // 文字总结
            'mind_map': 2,      // 思维导图
            'video_player': 3   // 播客
        };
        return typeMap[cardType] || 1;
    }, []);

    /**
     * 工具方法：将 task_status 转换为卡片状态
     */
    const convertTaskStatusToCardStatus = useCallback((taskStatus) => {
        // task_status: 0=未开始, 1=已完成, 2=进行中, 3=失败
        if (taskStatus === 0) {
            return 'idle'; // 未开始
        } else if (taskStatus === 1) {
            return 'success'; // 已完成
        } else if (taskStatus === 2) {
            return 'loading'; // 进行中
        } else if (taskStatus === 3) {
            return 'error'; // 失败
        }
        return 'idle'; // 默认未开始
    }, []);

    /**
     * 工具方法：从 task_list 初始化卡片状态
     */
    const initializeCardStatusFromTaskList = useCallback((task_list) => {
        const statusMap = {
            text_summary: 'idle',
            mind_map: 'idle',
            video_player: 'idle'
        };

        if (Array.isArray(task_list) && task_list.length > 0) {
            task_list.forEach((task) => {
                const taskType = Number(task.task_type);
                const taskStatus = Number(task.task_status);
                
                // task_type: 1=文字总结, 2=思维导图, 3=播客
                if (taskType === 1) {
                    statusMap.text_summary = convertTaskStatusToCardStatus(taskStatus);
                } else if (taskType === 2) {
                    statusMap.mind_map = convertTaskStatusToCardStatus(taskStatus);
                } else if (taskType === 3) {
                    statusMap.video_player = convertTaskStatusToCardStatus(taskStatus);
                }
            });
        }

        return statusMap;
    }, [convertTaskStatusToCardStatus]);

    /**
     * 工具方法：处理播客 transcript 数据
     */
    const processPodcastTranscript = useCallback((podcastContent) => {
        if (!Array.isArray(podcastContent)) return [];
        return podcastContent.map((item) => {
            const startTime = item.start_time || 0;
            const minutes = Math.floor(startTime / 60);
            const seconds = Math.floor(startTime % 60);
            return {
                text: item.text || '',
                startTime: startTime,
                time: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            };
        });
    }, []);

    /**
     * 工具方法：构建显示数据
     */
    const buildDisplayData = useCallback((cardType, apiResultData, ele = {}) => {
        let displayData = {
            type: cardType,
            title: cardType === 'text_summary' ? '文字总结' : 
                   cardType === 'mind_map' ? '思维导图' : '播客'
        };

        if (cardType === 'text_summary') {
            displayData.description = apiResultData?.content || apiResultData?.description || apiResultData?.summary || '';
        } else if (cardType === 'mind_map') {
            displayData.imageUrl = apiResultData?.image_url || apiResultData?.imageUrl || apiResultData?.image || '';
            displayData.description = apiResultData?.description || '这是一个思维导图，展示了主要知识点和它们之间的关系。';
            displayData.content = apiResultData?.content;
        } else if (cardType === 'video_player') {
            const cleanTitle = (title) => {
                if (!title) return '';
                return title.replace(/\*\*/g, '').trim();
            };
            const podcastContent = apiResultData?.podcast_content || [];
            const transcript = processPodcastTranscript(podcastContent);

            displayData.title = cleanTitle(apiResultData?.title || ele.title || '播客');
            displayData.audioUrl = apiResultData?.podcast_url || apiResultData?.audio_url || apiResultData?.audioUrl || apiResultData?.audio || '';
            displayData.transcript = transcript;
            displayData.thumbnail = apiResultData?.icon || apiResultData?.thumbnail || apiResultData?.thumbnail_url || '';
        }

        return displayData;
    }, [processPodcastTranscript]);

    /**
     * 工具方法：处理多模态引用
     * 将用户消息的 references 字段转换为 multimodal_reference 格式
     */
    const processMultimodalReferences = useCallback((temData, openHistoryMultimodalList) => {
        temData.forEach((item) => {
            if (item.role === 'local' && item.references && Array.isArray(item.references) && item.references.length > 0) {
                const videoReferences = item.references.filter((ref) => ref.image_url || ref.title);
                if (videoReferences.length > 0) {
                    item.multimodal_reference = {
                        title: '多模态学习',
                        description: '',
                        videoCount: videoReferences.length,
                        firstVideoTitle: videoReferences[0]?.title || '',
                        reference_id: item.reference_id || null,
                        // 列表按钮点击时打开视频列表
                        onListClick: () => openHistoryMultimodalList(videoReferences)
                    };
                }
            }
        });
    }, []);

    return {
        isSuccessStatus,
        isProcessingStatus,
        isFailedStatus,
        extractStatus,
        getTaskType,
        convertTaskStatusToCardStatus,
        initializeCardStatusFromTaskList,
        processPodcastTranscript,
        buildDisplayData,
        processMultimodalReferences
    };
};

export default useMultimodalUtils;

